import isObject from 'lodash/isObject';
import invert from 'lodash/invert';
import includes from 'lodash/includes';
import Carrier from './carriers/Carrier.enum';
import Service from './services/Service.enum';
import ServiceType from './services/ServiceType.enum';
import PackageType from './services/PackageType.enum';
import CalculatedData from './CalculatedData';

/**
 * This class provides helper functions to translate the data type between
 * SWSIM and SERA.
 */
export default class ServiceDataHelper {
	/**
	 * [SERA ServiceType]: [SWSIM ServiceType]
	 */
	static serviceLookup = {
		// USPS supported service types
		[ServiceType.UspsFirstClassMail]: 'USFC',
		[ServiceType.UspsGroundAdvantage]: 'USGA',
		[ServiceType.UspsPriorityMail]: 'USPM',
		[ServiceType.UspsPriorityMailExpress]: 'USXM',
		[ServiceType.UspsMediaMail]: 'USMM',
		[ServiceType.UspsParcelSelect]: 'USPS',
		[ServiceType.UspsFirstClassMailIntl]: 'USFCI',
		[ServiceType.UspsPriorityMailIntl]: 'USPMI',
		[ServiceType.UspsPriorityMailExpressIntl]: 'USEMI',

		// UPS supported service types
		[ServiceType.UpsNextDayAirEarly]: 'UPSNDAE',
		[ServiceType.UpsNextDayAir]: 'UPSNDA',
		[ServiceType.UpsNextDayAirSaver]: 'UPSNDAS',
		[ServiceType.Ups2ndDayAirAm]: 'UPS2DAA',
		[ServiceType.Ups2ndDayAir]: 'UPS2DA',
		[ServiceType.Ups3DaySelect]: 'UPS3DS',
		[ServiceType.UpsGround]: 'UPSGD',
		[ServiceType.UpsGroundSaver]: 'UPSGDS',
		[ServiceType.UpsStandard]: 'UPSSD',
		[ServiceType.UpsWorldwideExpress]: 'UPSWES',
		[ServiceType.UpsWorldwideExpressPlus]: 'UPSWESP',
		[ServiceType.UpsWorldwideExpedited]: 'UPSWED',
		[ServiceType.UpsWorldwideSaver]: 'UPSWSR',

		// DHL Express supported service types
		[ServiceType.DhlExpressWorldwide]: 'DHLEWW',

		// FedEx supported service types
		[ServiceType.FedExFirstOvernight]: 'FXFO',
		[ServiceType.FedExPriorityOvernight]: 'FXPO',
		[ServiceType.FedExStandardOvernight]: 'FXSO',
		[ServiceType.FedEx2DayAm]: 'FX2DAM',
		[ServiceType.FedEx2Day]: 'FX2D',
		[ServiceType.FedExExpressSaver]: 'FXES',
		[ServiceType.FedExGround]: 'FXGD',
		[ServiceType.FedExHomeDelivery]: 'FXHD',
		[ServiceType.FedExIntlFirst]: 'FXFI',
		[ServiceType.FedExIntlPriority]: 'FXPI',
		[ServiceType.FedExIntlEconomy]: 'FXEI',
		[ServiceType.FedExIntlGround]: 'FXGDI',

		// GlobalPost supported service types
		[ServiceType.GlobalPostFirstClassSmartSaver]: 'SCGPFCSS',
		[ServiceType.GlobalPostParcelSelectSmartSaver]: 'SCGPPSSS',
		[ServiceType.GlobalPostEconomyIntl]: 'SCGPE',
		[ServiceType.GlobalPostEconomyIntlSmartSaver]: 'SCGPESS',
		[ServiceType.GlobalPostStandardIntl]: 'SCGPP',
		[ServiceType.GlobalPostStandardIntlSmartSaver]: 'SCGPPSS',
		[ServiceType.GlobalPostPlus]: 'SCGPL',
		[ServiceType.GlobalPostPlusSmartSaver]: 'SCGPLSS',
		[ServiceType.GlobalPostFirstClassIntl]: 'SCGPFCI',
		[ServiceType.GlobalPostFirstClassIntlSmartSaver]: 'SCGPFCISS',
		[ServiceType.GlobalPostPriorityMailIntl]: 'SCGPPMI',
		[ServiceType.GlobalPostPriorityMailIntlSmartSaver]: 'SCGPPMISS',
		[ServiceType.GlobalPostPriorityMailExpressIntl]: 'SCGPEMI',
		[ServiceType.GlobalPostPriorityMailExpressIntlSmartSaver]: 'SCGPEMISS',
	};

	/**
	 * [Carrier]: {
	 *    [SERA PackageType]: [SWSIM PackageType]
	 * }
	 */
	static packageLookup = {
		// USPS supported package types
		[Carrier.USPS]: {
			[PackageType.Postcard]: 'Postcard',
			[PackageType.Envelope]: 'Envelope',
			[PackageType.LargeEnvelope]: 'LargeEnvelopeorFlat',
			[PackageType.Package]: 'Package',
			[PackageType.UspsSmallFlatRateBox]: 'SmallFlatRateBox',
			[PackageType.UspsMediumFlatRateBox]: 'FlatRateBox',
			[PackageType.UspsLargeFlatRateBox]: 'LargeFlatRateBox',
			[PackageType.UspsFlatRateEnvelope]: 'FlatRateEnvelope',
			[PackageType.UspsPaddedFlatRateEnvelope]: 'FlatRatePaddedEnvelope',
			[PackageType.UspsLegalFlatRateEnvelope]: 'LegalFlatRateEnvelope',
		},
		// UPS supported package types
		[Carrier.UPS]: {
			[PackageType.Package]: 'Package',
			[PackageType.UpsLetter]: 'Letter',
			[PackageType.UpsPak]: 'Pak',
			[PackageType.UpsTube]: 'Tube',
			[PackageType.UpsExpressBoxSmall]: 'ExpressBoxSmall',
			[PackageType.UpsExpressBoxMedium]: 'ExpressBoxMedium',
			[PackageType.UpsExpressBoxLarge]: 'ExpressBoxLarge',
			[PackageType.Ups10kgBox]: '10KGBox',
			[PackageType.Ups25kgBox]: '25KGBox',
		},
		// FedEx supported package types
		[Carrier.FedEx]: {
			[PackageType.Package]: 'Package',
			[PackageType.FedExEnvelope]: 'Envelope',
			[PackageType.FedExPak]: 'Pak',
			[PackageType.FedExTube]: 'Tube',
			[PackageType.FedExOneRateEnvelope]: 'OneRateEnvelope',
			[PackageType.FedExOneRatePak]: 'OneRatePak',
			[PackageType.FedExOneRateTube]: 'OneRateTube',
			[PackageType.FedExOneRateSmallBox]: 'OneRateSmallBox',
			[PackageType.FedExOneRateMediumBox]: 'OneRateMediumBox',
			[PackageType.FedExOneRateLargeBox]: 'OneRateLargeBox',
			[PackageType.FedExOneRateExtraLargeBox]: 'OneRateExtraLargeBox',
			[PackageType.FedEx10kgBox]: '10KGBox',
			[PackageType.FedEx25kgBox]: '25KGBox',
		},
		// DHL Express supported package types
		[Carrier.DHLExpress]: {
			[PackageType.ExpressEnvelope]: 'ExpressEnvelope',
			[PackageType.Package]: 'Package',
		},
	};

	/**
	 * Return SERA Service Type (in SERA) based on SWSIM Service Type.
	 * @param {string} swsimServiceType The SWSIM service type.
	 * @return {ServiceType}
	 */
	static translateServiceTypeToSERA = swsimServiceType => {
		const lookup = invert(this.serviceLookup);

		return lookup[swsimServiceType];
	};

	/**
	 * Return SWSIM Service Type based on SERA Service Type.
	 * @param {string} seraServiceType The SERA service type.
	 * @return {ServiceType}
	 */
	static translateServiceTypeToSWSIM = seraServiceType => this.serviceLookup[seraServiceType];

	/**
	 * Return SERA Package Type based on SWSIM Package Type.
	 * @param {Carrier} carrier Carrier enum value.
	 * @param {string} swsimPackageType The SWSIM package type.
	 * @param {string} swsimPackageType The SWSIM package type.
	 * @return {PackageType}
	 */
	static translatePackageTypeToSERA = (carrier, swsimPackageType) => {
		//  USPS and GlobalPost share the same lookup object
		const carrierLookupKey = carrier === Carrier.GlobalPost ? Carrier.USPS : carrier;
		const lookup = invert(this.packageLookup[carrierLookupKey]);

		// 'ThickEnvelope' In SWSIM is 'package' in SERA
		if (carrierLookupKey === Carrier.USPS) {
			return swsimPackageType === 'ThickEnvelope'
				? PackageType.Package
				: lookup[swsimPackageType];
		}

		return lookup[swsimPackageType];
	};

	/**
	 * Return SWSIM Package Type based on SERA Package Type.
	 * @param {Carrier} carrier Carrier enum value.
	 * @param {string} serviceType The SERA service type.
	 * @param {string} packageType The SERA package type.
	 * @return {PackageType}
	 */
	static translatePackageTypeToSWSIM = (carrier, serviceType, packageType, dimensions) => {
		//  USPS and GlobalPost share the same lookup object
		const carrierLookupKey = carrier === Carrier.GlobalPost ? Carrier.USPS : carrier;

		// sort dimensions in an ascending order
		const sortedDimensions = isObject(dimensions)
			? [dimensions.width, dimensions.height, dimensions.length].sort((n1, n2) => n1 - n2)
			: [0, 0, 0];

		// calculate total dimensions
		const totalDimensions = sortedDimensions[0] * sortedDimensions[1] * sortedDimensions[2];

		// calculate girth + length
		const girthPlusLength =
			(sortedDimensions[0] + sortedDimensions[1]) * 2 + sortedDimensions[2];

		// In SWSIM, the following services don't support 'Package' but 'ThickEnvelope'
		if (carrierLookupKey === Carrier.USPS && packageType === PackageType.Package) {
			if (
				includes(
					[
						ServiceType.UspsFirstClassMail,
						ServiceType.UspsFirstClassMailIntl,
						ServiceType.GlobalPostEconomyIntl,
						ServiceType.GlobalPostEconomyIntlSmartSaver,
					],
					serviceType
				)
			) {
				return 'ThickEnvelope';
			}

			// per WEBAPPS-31287, package type Translation Logic (SERA -> SWSIM)
			if (
				serviceType === ServiceType.UspsPriorityMail ||
				serviceType === ServiceType.UspsPriorityMailExpress
			) {
				return totalDimensions > 1728
					? 'LargePackage'
					: this.packageLookup[carrierLookupKey][packageType];
			} else if (serviceType === ServiceType.UspsParcelSelect) {
				if (totalDimensions > 1728) {
					return girthPlusLength > 108 ? 'OversizedPackage' : 'LargePackage';
				} else {
					return this.packageLookup[carrierLookupKey][packageType];
				}
			}
		}

		return this.packageLookup[carrierLookupKey][packageType];
	};

	/**
	 * Return the service data SWSIM can recognize.
	 * @param {object} serviceData The static service data object. See ServiceUtil.buildAllServices method.
	 * @return {object}
	 */
	static translateDataToSWSIM = (serviceData, dimensions) => {
		const { carrier, serviceType, packageType } = serviceData;

		return {
			carrier: this.getCarrierKey(carrier),
			serviceType: this.translateServiceTypeToSWSIM(serviceType),
			packageType: this.translatePackageTypeToSWSIM(
				carrier,
				serviceType,
				packageType,
				dimensions
			),
		};
	};

	/**
	 * Return Service enum key based on the service type (in SWSIM) and package type (in SWSIM).
	 * @param {string} serviceType The service Type (in SWSIM).
	 * @param {string} packageType The package Type (in SWSIM).
	 * @returns {string}
	 */
	static getServiceKey = (serviceType, packageType) => {
		const carrier = this.getCarrierValue(serviceType);
		const seraServiceType = this.translateServiceTypeToSERA(serviceType);
		const seraPackageType = this.translatePackageTypeToSERA(packageType, carrier);

		// return a new object of inverted keys and values of Service enum object
		const dictionary = invert(Service);

		return dictionary[`${seraServiceType}_${seraPackageType}`];
	};

	/**
	 * Return the service static object (in SERA).
	 * @param {string} serviceType The service Type (in SWSIM).
	 * @param {string} packageType The package Type (in SWSIM).
	 * @returns {object|*}
	 */
	static getServiceStaticData = (serviceType, packageType) => {
		const serviceKey = this.getServiceKey(serviceType, packageType);

		return CalculatedData.allServicesMap[serviceKey];
	};

	/**
	 * Return Carrier enum key.
	 * @param {Carrier} carrier The Carrier enum value.
	 * @returns {string}
	 */
	static getCarrierKey = carrier => {
		// return a new object of inverted keys and values of Carrier enum object
		const dictionary = invert(Carrier);

		return dictionary[carrier];
	};

	/**
	 * Return the Carrier key (in SERA) based on the service type (in SWSIM).
	 * @param {string} serviceType The service type (in SWSIM).
	 * @returns {string}
	 */
	static getCarrierValue = serviceType => {
		const dictionary = {
			// USPS supported service types
			USFC: Carrier.USPS,
			USGA: Carrier.USPS,
			USPM: Carrier.USPS,
			USXM: Carrier.USPS,
			USMM: Carrier.USPS,
			USPS: Carrier.USPS,
			USFCI: Carrier.USPS,
			USPMI: Carrier.USPS,
			USEMI: Carrier.USPS,

			// UPS supported service types
			UPSNDAE: Carrier.UPS,
			UPSNDA: Carrier.UPS,
			UPSNDAS: Carrier.UPS,
			UPS2DAA: Carrier.UPS,
			UPS2DA: Carrier.UPS,
			UPS3DS: Carrier.UPS,
			UPSGD: Carrier.UPS,
			UPSGDS: Carrier.UPS,
			UPSSD: Carrier.UPS,
			UPSWES: Carrier.UPS,
			UPSWESP: Carrier.UPS,
			UPSWED: Carrier.UPS,
			UPSWSR: Carrier.UPS,

			// DHL Express supported service types
			DHLEWW: Carrier.DHLExpress,

			// FedEx supported service types
			FXFO: Carrier.FedEx,
			FXPO: Carrier.FedEx,
			FXSO: Carrier.FedEx,
			FX2DAM: Carrier.FedEx,
			FX2D: Carrier.FedEx,
			FXES: Carrier.FedEx,
			FXGD: Carrier.FedEx,
			FXHD: Carrier.FedEx,
			FXFI: Carrier.FedEx,
			FXPI: Carrier.FedEx,
			FXEI: Carrier.FedEx,
			FXGDI: Carrier.FedEx,

			// GlobalPost supported service types
			SCGPFCSS: Carrier.GlobalPost,
			SCGPPSSS: Carrier.GlobalPost,
			SCGPE: Carrier.GlobalPost,
			SCGPESS: Carrier.GlobalPost,
			SCGPP: Carrier.GlobalPost,
			SCGPPSS: Carrier.GlobalPost,
			GlobalPostPlus: Carrier.GlobalPost,
			SCGPLSS: Carrier.GlobalPost,
			SCGPFCI: Carrier.GlobalPost,
			SCGPFCISS: Carrier.GlobalPost,
			SCGPPMI: Carrier.GlobalPost,
			SCGPPMISS: Carrier.GlobalPost,
			SCGPEMI: Carrier.GlobalPost,
			SCGPEMISS: Carrier.GlobalPost,
		};

		return dictionary[serviceType];
	};

	/**
	 * Return carrier's display name.
	 * @param {Carrier} carrier The carrier enum value.
	 * @returns {string}
	 */
	static getCarrierDisplayName = carrier => {
		const carrierKey = this.getCarrierKey(carrier);
		const carrierData = CalculatedData.allCarriersMap[carrierKey];

		return isObject(carrierData) ? carrierData.displayName : '';
	};
}
